import React, { useEffect, useState } from "react";
import { Route, Switch, withRouter, useHistory } from "react-router-dom";
import classnames from "classnames";
import { Box, Grid, CircularProgress } from "@mui/material";
import jwt_decode from "jwt-decode";
import { gql, useLazyQuery, useQuery } from "@apollo/client";

import useStyles from "./styles";
import Header from "../Header";
import Sidebar from "../Sidebar";

import Dashboard from "../../pages/dashboard/Dashboard";
import Tables from "../../pages/tables/Tables";
import Charts from "../../pages/charts/Charts";
import DeviceGroup from "../../pages/maps/devicegroup";
import ManagerTable from "../../pages/compaign/managerTable";
import CertificationManager from "../../pages/CertificationManager/CertificationManager";
import CertificationViewer from "../../pages/CertificationViewer/CertificationViewer";
import VinImeMap from "../../pages/Vehicles";
import KycDetails from "../../pages/KYC_Details";
import TicketCreation from "../../pages/Ticket_Creation";
import FotaManager from "../../pages/FOTA";
import VinKycLogs from "../../pages/VinImeLogs";
import ECU from "../../pages/ECU_OTA";
import VinsByCampaign from "../../pages/ECU_OTA/VinsByCampaign";
import ServiceUserL2 from "../../pages/ServiceUserL2/ServiceUserL2";
import ServiceUserLogs from "../../pages/ServiceUserLogs/ServiceUserLogs";
import DealerLogins from "../../pages/DealerLogins";
import EoltReport from "../../pages/EOLTReport";

import { PageNotFound } from "./PageNotFound";
import { useLayoutState } from "../../context/LayoutContext";
import { useUserDispatch, signOut } from "../../context/UserContext";

/* -------------------- GraphQL -------------------- */

const getUsersQuery = gql`
  query ($id: Int) {
    getUsers(id: $id) {
      id
      username
      role_id
      status
      ownerId
      categories {
        id
        iconPath
        name
        tooltip
        features {
          id
          category_id
          iconPath
          name
          tooltip
          selectedIconPath
        }
      }
    }
  }
`;

const getOemQuery = gql`
  query {
    getOem {
      id
      oemName
      oemCode
      oemEmail
      phoneNumber
      logo
    }
  }
`;

function Layout(props) {
  const classes = useStyles();
  const history = useHistory();
  const layoutState = useLayoutState();
  const userDispatch = useUserDispatch();

  /* -------------------- STATE -------------------- */

  const [categories, setCategories] = useState([]);
  const [featuresForDLM, setFeaturesForDLM] = useState([]);
  const [featuresForCM, setFeaturesForCM] = useState([]);
  const [featuresForOTA, setFeaturesForOTA] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [loginUser, setLoginUser] = useState(null);
  const [oem, setOem] = useState(null);

  /* -------------------- AUTH -------------------- */

  const token = localStorage.getItem("id_token");
  const decoded = token ? jwt_decode(token) : null;

  useEffect(() => {
    if (!token || !decoded) {
      localStorage.clear();
      history.push("/login");
      return;
    }

    const currentTimestamp = Math.floor(Date.now() / 1000);
    if (decoded.exp && currentTimestamp > decoded.exp) {
      localStorage.clear();
      history.push("/login");
    }
  }, []);

  /* -------------------- OEM -------------------- */

  const { loading: oemLoading, data: oemData } = useQuery(getOemQuery, {
    fetchPolicy: "network-only",
  });

  /* -------------------- USERS / FEATURES -------------------- */

  const [getUsers] = useLazyQuery(getUsersQuery, {
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      if (!res?.getUsers?.length) return;

      const dlm = [];
      const cm = [];
      const ota = [];
      const categoryList = [];
      const allFeatureNames = [];

      res.getUsers.forEach((user) => {
        setLoginUser(user.username);

        user.categories.forEach((cat) => {
          categoryList.push({
            id: cat.id,
            iconPath: cat.iconPath,
            name: cat.name,
            tooltip: cat.tooltip,
          });

          cat.features.forEach((f) => {
            allFeatureNames.push(f.name.toLowerCase());

            if (cat.id === 1) dlm.push(f);
            if (cat.id === 2) cm.push(f);
            if (cat.id === 3) ota.push(f);
          });
        });
      });

      setCategories(categoryList);
      setFeaturesForDLM(dlm);
      setFeaturesForCM(cm);
      setFeaturesForOTA(ota);
      setAllFeatures(allFeatureNames);

      console.log("DLM:", dlm);
      console.log("CM:", cm);
      console.log("OTA:", ota);
    },
  });

  useEffect(() => {
    if (decoded?.id) {
      getUsers({ variables: { id: decoded.id } });
    }
  }, []);

  const handleCategoty = (e) => {
  localStorage.setItem("categories", e);

  if (e === 1 || e === "1") {
    if (featuresForDLM.length > 0) {
      history.push(`/app/${featuresForDLM[0].name.toLowerCase()}`);
    }
  } else if (e === 2 || e === "2") {
    if (featuresForCM.length > 0) {
      history.push(`/app/${featuresForCM[0].name.toLowerCase()}`);
    }
  } else if (e === 3 || e === "3") {
    if (featuresForOTA.length > 0) {
      history.push(`/app/${featuresForOTA[0].name.toLowerCase()}`);
    }
  }
};


  /* -------------------- CATEGORY -------------------- */

  const categoryId = localStorage.getItem("categories") || "1";

  const isFeaturesReady =
    featuresForDLM.length > 0 ||
    featuresForCM.length > 0 ||
    featuresForOTA.length > 0;

  /* -------------------- RENDER -------------------- */

  return (
    <div className={classes.root}>
      {oemLoading ? (
        <CircularProgress color="secondary" />
      ) : (
        <Header
          history={props.history}
          data={{ categories, oems: oemData?.getOem }}
          token={decoded}
          getOem={(val) => setOem(val)}
          handleCategoty={handleCategoty}
        />
      )}

      {isFeaturesReady && (
        categoryId === "3" ? (
          <Sidebar data={featuresForOTA} />
        ) : categoryId === "2" ? (
          <Sidebar data={featuresForCM} />
        ) : (
          <Sidebar data={featuresForDLM} />
        )
      )}

      <div
        className={classnames(classes.content, {
          [classes.contentShift]: layoutState.isSidebarOpened,
        })}
      >
        <div className={classes.fakeToolbar} />

        <Switch>
          <Route path="/app/summary" render={() => <Dashboard oem={oem} />} />
          <Route
            path="/app/devicemanager"
            render={() => <Tables oem={oem} loginUser={loginUser} />}
          />
          <Route
            path="/app/sim-management"
            render={() => <Charts oem={oem} loginUser={loginUser} />}
          />
          <Route
            path="/app/device-group-manager"
            render={() => <DeviceGroup oem={oem} />}
          />
          <Route
            path="/app/ota-campaign"
            render={() => <ManagerTable oem={oem} />}
          />
          <Route
            path="/app/certification manager"
            render={() => <CertificationManager oem={oem} token={decoded} />}
          />
          <Route
            path="/app/certification viewer"
            render={() => <CertificationViewer oem={oem} token={decoded} />}
          />
          <Route
            path="/app/vehicles"
            render={() => <VinImeMap oem={oem} token={decoded} />}
          />
          <Route
            path="/app/kyc-details"
            render={() => <KycDetails oem={oem} />}
          />
          <Route
            path="/app/Support-Ticket"
            render={() => <TicketCreation oem={oem} token={decoded} />}
          />
          <Route
            path="/app/FOTA-Manager"
            render={() => <FotaManager oem={oem} />}
          />
          <Route
            path="/app/Vin-Kyc-logs"
            render={() => <VinKycLogs oem={oem} />}
          />
          <Route
            path="/app/ecu-ota"
            render={(props) => <ECU {...props} oem={oem} token={decoded} />}
          />
          <Route
            path="/app/vinCampaign"
            render={(props) => <VinsByCampaign {...props} oem={oem} />}
          />
          <Route
            path="/app/ticket-manager"
            render={(props) => (
              <ServiceUserL2 {...props} oem={oem} token={decoded} />
            )}
          />
          <Route
            path="/app/service-tickets-logs"
            render={(props) => (
              <ServiceUserLogs {...props} oem={oem} token={decoded} />
            )}
          />
          <Route
            path="/app/dealer-login"
            render={(props) => (
              <DealerLogins {...props} oem={oem} token={decoded} />
            )}
          />
          <Route
            path="/app/eolt-report"
            render={(props) => (
              <EoltReport {...props} oem={oem} token={decoded} />
            )}
          />
          <Route component={PageNotFound} />
        </Switch>

        <Box mt={5} />
      </div>
    </div>
  );
}

export default withRouter(Layout);
